import React from 'react';
import { Redirect } from 'react-router-dom';
import { Route } from 'react-router-dom';
import * as FeatherIcon from 'react-feather';

import { isUserAuthenticated, getLoggedInUser } from '../helpers/authUtils';

// auth
const Login = React.lazy(() => import('../pages/auth/Login'));
const Logout = React.lazy(() => import('../pages/auth/Logout'));
const Register = React.lazy(() => import('../pages/auth/Register'));
const ForgetPassword = React.lazy(() => import('../pages/auth/ForgetPassword'));
const Confirm = React.lazy(() => import('../pages/auth/Confirm'));

// dashboard
const Dashboard = React.lazy(() => import('../pages/dashboard'));

// chat
const Chat = React.lazy(() => import('../pages/chat'));

// campaigns
const Campaigns = React.lazy(() => import('../pages/campaigns'));
const ContactLists = React.lazy(() => import('../pages/campaigns/ContactLists'));

// webhooks
const Webhooks = React.lazy(() => import('../pages/webhooks'));

// monitoring
const Monitoring = React.lazy(() => import('../pages/monitoring'));

// calls
const CallHistory = React.lazy(() => import('../pages/calls'));

// debug
const DebugPage = React.lazy(() => import('../pages/debug'));

// devices, contacts, status
const Devices = React.lazy(() => import('../pages/devices'));
const Contacts = React.lazy(() => import('../pages/contacts'));
const StatusViewer = React.lazy(() => import('../pages/status/statusviewer'));
const StatusSender = React.lazy(() => import('../pages/status/statusender'));

// apps (template pages)
const CalendarApp = React.lazy(() => import('../pages/apps/Calendar'));
const EmailInbox = React.lazy(() => import('../pages/apps/Email/Inbox'));
const EmailDetail = React.lazy(() => import('../pages/apps/Email/Detail'));
const EmailCompose = React.lazy(() => import('../pages/apps/Email/Compose'));
const ProjectList = React.lazy(() => import('../pages/apps/Project/List'));
const ProjectDetail = React.lazy(() => import('../pages/apps/Project/Detail/'));
const TaskList = React.lazy(() => import('../pages/apps/Tasks/List'));
const TaskBoard = React.lazy(() => import('../pages/apps/Tasks/Board'));

// pages
const Starter = React.lazy(() => import('../pages/other/Starter'));
const Profile = React.lazy(() => import('../pages/other/Profile/'));
const Activity = React.lazy(() => import('../pages/other/Activity'));
const Invoice = React.lazy(() => import('../pages/other/Invoice'));
const Pricing = React.lazy(() => import('../pages/other/Pricing'));
const Error404 = React.lazy(() => import('../pages/other/Error404'));
const Error500 = React.lazy(() => import('../pages/other/Error500'));

// ui
const BSComponents = React.lazy(() => import('../pages/uikit/BSComponents/'));
const FeatherIcons = React.lazy(() => import('../pages/uikit/Icons/Feather'));
const UniconsIcons = React.lazy(() => import('../pages/uikit/Icons/Unicons'));
const Widgets = React.lazy(() => import('../pages/uikit/Widgets/'));

// charts
const Charts = React.lazy(() => import('../pages/charts/'));

// forms
const BasicForms = React.lazy(() => import('../pages/forms/Basic'));
const FormAdvanced = React.lazy(() => import('../pages/forms/Advanced'));
const FormValidation = React.lazy(() => import('../pages/forms/Validation'));
const FormWizard = React.lazy(() => import('../pages/forms/Wizard'));
const FileUpload = React.lazy(() => import('../pages/forms/FileUpload'));
const Editor = React.lazy(() => import('../pages/forms/Editor'));

// tables
const BasicTables = React.lazy(() => import('../pages/tables/Basic'));
const AdvancedTables = React.lazy(() => import('../pages/tables/Advanced'));
const AdvancedTables2 = React.lazy(() => import('../pages/tables/Advanced2'));

// chatbot
const ChatbotPage = React.lazy(() => import('../pages/chatbot'));

// handle auth and authorization
const PrivateRoute = ({ component: Component, roles, ...rest }) => (
    <Route
        {...rest}
        render={(props) => {
            if (!isUserAuthenticated()) {
                return <Redirect to={{ pathname: '/account/login', state: { from: props.location } }} />;
            }
            const loggedInUser = getLoggedInUser();
            if (roles && roles.indexOf(loggedInUser.role) === -1) {
                return <Redirect to={{ pathname: '/' }} />;
            }
            return <Component {...props} />;
        }}
    />
);

// root routes
const rootRoute = {
    path: '/',
    exact: true,
    component: () => <Redirect to="/painel" />,
    route: PrivateRoute,
};

// =============================================
// MENU STRUCTURE
// =============================================

// -- Estatisticas --
const dashboardRoutes = {
    path: '/painel',
    name: 'Painel',
    icon: FeatherIcon.Home,
    header: 'Estatisticas',
    component: Dashboard,
    route: PrivateRoute,
};

// -- WhatsApp --
const chatRoutes = {
    path: '/chat',
    name: 'Chat',
    icon: FeatherIcon.MessageCircle,
    header: 'WhatsApp',
    component: Chat,
    route: PrivateRoute,
};

const devicesRoutes = {
    path: '/devices',
    name: 'Dispositivos',
    icon: FeatherIcon.Smartphone,
    component: Devices,
    route: PrivateRoute,
};

const contactRoutes = {
    path: '/contacts',
    name: 'Contatos',
    icon: FeatherIcon.Users,
    component: Contacts,
    route: PrivateRoute,
};

// -- Campanhas --
const campaignsRoutes = {
    path: '/campanhas',
    name: 'Campanhas',
    icon: FeatherIcon.Send,
    header: 'Campanhas',
    component: Campaigns,
    route: PrivateRoute,
};

const contactListsRoutes = {
    path: '/listas-contatos',
    name: 'Listas de Contatos',
    icon: FeatherIcon.List,
    component: ContactLists,
    route: PrivateRoute,
};

// -- Status --
const statusRoutes = {
    path: '/status',
    name: 'Status',
    icon: FeatherIcon.Circle,
    header: 'Status',
    children: [
        {
            path: '/send-status',
            name: 'Enviar Status',
            component: StatusSender,
            route: PrivateRoute,
        },
        {
            path: '/view-status',
            name: 'Ver Status',
            component: StatusViewer,
            route: PrivateRoute,
        },
    ],
};

// -- Ferramentas --
const msgAutoRoutes = {
    path: '/msgauto',
    name: 'Mensagem automatica',
    header: 'Ferramentas',
    icon: FeatherIcon.MessageSquare,
    component: AdvancedTables2,
    route: PrivateRoute,
};

const chatBotRoutes = {
    path: '/chatbot-flow',
    name: 'Chatbot Flow',
    header: 'Automacao',
    icon: FeatherIcon.Command,
    component: ChatbotPage,
    route: PrivateRoute,
};

const ferrGruposRoutes = {
    path: '/ferramentas-grupos',
    name: 'Ferramentas Grupos',
    icon: FeatherIcon.Tool,
    component: AdvancedTables,
    route: PrivateRoute,
};

const linkUnicoRoutes = {
    path: '/link-unico',
    name: 'Link unico grupos',
    icon: FeatherIcon.Link,
    component: BasicTables,
    route: PrivateRoute,
};

// -- Monitoramento --
const monitoringRoutes = {
    path: '/monitoramento',
    name: 'Monitoramento',
    icon: FeatherIcon.Activity,
    header: 'Sistema',
    component: Monitoring,
    route: PrivateRoute,
};

const callHistoryRoutes = {
    path: '/chamadas',
    name: 'Chamadas',
    icon: FeatherIcon.Phone,
    component: CallHistory,
    route: PrivateRoute,
};

const debugRoutes = {
    path: '/debug',
    name: 'Debug & Logs',
    icon: FeatherIcon.Terminal,
    component: DebugPage,
    route: PrivateRoute,
};

// -- Integracao --
const webhooksRoutes = {
    path: '/webhooks',
    name: 'Webhooks & API',
    icon: FeatherIcon.Link2,
    header: 'Integracao',
    component: Webhooks,
    route: PrivateRoute,
};

// -- Notificacoes --
const emailAppRoutes = {
    path: '/mensagens/',
    name: 'Notificacoes Push',
    header: 'Notificacoes',
    icon: FeatherIcon.Bell,
    children: [
        {
            path: '/mensagens/nova',
            name: 'Enviar nova',
            component: EmailCompose,
            route: PrivateRoute,
        },
        {
            path: '/mensagens/historico',
            name: 'Historico',
            component: ProjectList,
            route: PrivateRoute,
        },
    ],
};

// -- Template pages (kept as examples) --
const pagesRoutes = {
    path: '/pages',
    name: 'Pages',
    header: 'Templates',
    icon: FeatherIcon.FileText,
    children: [
        { path: '/pages/starter', name: 'Starter', component: Starter, route: PrivateRoute },
        { path: '/pages/profile', name: 'Profile', component: Profile, route: PrivateRoute },
        { path: '/pages/activity', name: 'Activity', component: Activity, route: PrivateRoute },
        { path: '/pages/invoice', name: 'Invoice', component: Invoice, route: PrivateRoute },
        { path: '/pages/pricing', name: 'Pricing', component: Pricing, route: PrivateRoute },
        { path: '/pages/error-404', name: 'Error 404', component: Error404, route: Route },
        { path: '/pages/error-500', name: 'Error 500', component: Error500, route: Route },
    ],
};

const componentsRoutes = {
    path: '/ui',
    name: 'UI Elements',
    icon: FeatherIcon.Package,
    children: [
        { path: '/ui/bscomponents', name: 'Bootstrap UI', component: BSComponents, route: PrivateRoute },
        {
            path: '/ui/icons',
            name: 'Icons',
            children: [
                { path: '/ui/icons/feather', name: 'Feather Icons', component: FeatherIcons, route: PrivateRoute },
                { path: '/ui/icons/unicons', name: 'Unicons Icons', component: UniconsIcons, route: PrivateRoute },
            ],
        },
        { path: '/ui/widgets', name: 'Widgets', component: Widgets, route: PrivateRoute },
    ],
};

const chartTemplateRoutes = {
    path: '/charts',
    name: 'Charts',
    component: Charts,
    icon: FeatherIcon.PieChart,
    route: PrivateRoute,
};

const formsRoutes = {
    path: '/forms',
    name: 'Forms',
    icon: FeatherIcon.Edit3,
    children: [
        { path: '/forms/basic', name: 'Basic', component: BasicForms, route: PrivateRoute },
        { path: '/forms/advanced', name: 'Advanced', component: FormAdvanced, route: PrivateRoute },
        { path: '/forms/validation', name: 'Validation', component: FormValidation, route: PrivateRoute },
        { path: '/forms/wizard', name: 'Wizard', component: FormWizard, route: PrivateRoute },
        { path: '/forms/editor', name: 'Editor', component: Editor, route: PrivateRoute },
        { path: '/forms/upload', name: 'File Upload', component: FileUpload, route: PrivateRoute },
    ],
};

const tableRoutes = {
    path: '/tables',
    name: 'Tables',
    icon: FeatherIcon.Grid,
    children: [
        { path: '/tables/basic', name: 'Basic', component: BasicTables, route: PrivateRoute },
        { path: '/tables/advanced', name: 'Advanced', component: AdvancedTables, route: PrivateRoute },
    ],
};

// auth
const authRoutes = {
    path: '/account',
    name: 'Auth',
    children: [
        { path: '/account/login', name: 'Login', component: Login, route: Route },
        { path: '/account/logout', name: 'Logout', component: Logout, route: Route },
        { path: '/account/register', name: 'Register', component: Register, route: Route },
        { path: '/account/confirm', name: 'Confirm', component: Confirm, route: Route },
        { path: '/account/forget-password', name: 'Forget Password', component: ForgetPassword, route: Route },
    ],
};

// flatten the list of all nested routes
const flattenRoutes = (routes) => {
    let flatRoutes = [];
    routes = routes || [];
    routes.forEach((item) => {
        flatRoutes.push(item);
        if (typeof item.children !== 'undefined') {
            flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)];
        }
    });
    return flatRoutes;
};

// All routes (includes template pages for routing - but NOT shown in sidebar menu)
const allRoutes = [
    rootRoute,
    // -- Paginas REAIS --
    dashboardRoutes,
    chatRoutes,
    devicesRoutes,
    contactRoutes,
    campaignsRoutes,
    contactListsRoutes,
    statusRoutes,
    monitoringRoutes,
    callHistoryRoutes,
    debugRoutes,
    webhooksRoutes,
    // -- Template pages (rotas existem mas NAO aparecem no menu) --
    pagesRoutes,
    componentsRoutes,
    chartTemplateRoutes,
    formsRoutes,
    tableRoutes,
    msgAutoRoutes,
    chatBotRoutes,
    ferrGruposRoutes,
    linkUnicoRoutes,
    emailAppRoutes,
    // Auth
    authRoutes,
];

// MENU SIDEBAR - SOMENTE paginas reais que funcionam de verdade
const authProtectedRoutes = [
    dashboardRoutes,
    chatRoutes,
    devicesRoutes,
    chatBotRoutes,
    monitoringRoutes,
    callHistoryRoutes,
    debugRoutes,
];

const allFlattenRoutes = flattenRoutes(allRoutes);
export { allRoutes, authProtectedRoutes, allFlattenRoutes };
